import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';
import BillWSChatHeader from './BillWSChatHeader';
import BillWSChatMessages from './BillWSChatMessages';
import BillWSChatInput from './BillWSChatInput';
import { useBillWSWorkflow } from './hooks/useBillWSWorkflow';

/**
 * Bill Processing WebSocket Chat Page
 * Dedicated chat page for WebSocket-based bill processing (Workflow 22)
 */
const BillWSChatPage = () => {
  const { id: urlId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Get params from URL
  const workflowName = searchParams.get('name') || 'Bill Processing (WebSocket)';
  const agentId = searchParams.get('agentId');

  // Determine if URL id is a run_id (UUID) or workflow_id (number)
  // UUID pattern: 8-4-4-4-12 hex chars
  const isUUID = urlId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(urlId);
  const workflowId = isUUID ? '22' : (urlId || '22');  // Default workflow 22 for bill processing
  const initialRunId = isUUID ? urlId : searchParams.get('run_id');

  // WebSocket workflow hook - pure WebSocket, no polling
  const {
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    sendMessage,
    error,
    resetWorkflow,
    hasActiveWorkflow,
    wsConnected
  } = useBillWSWorkflow(workflowId, token, initialRunId);

  // Cleanup on page unload
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasActiveWorkflow) {
        console.log('🧹 BillWS: Page unloading - cleaning up');
        resetWorkflow();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasActiveWorkflow, resetWorkflow]);

  // Handle back navigation
  const handleBack = () => {
    if (agentId) {
      navigate(`/agents/${agentId}`);
    } else {
      navigate('/workflows');
    }
  };

  // Handle debug
  const handleDebug = () => {
    if (runId) {
      navigate(`/debug?execution=${runId}&type=ws`);
    }
  };

  if (!token) {
    return (
      <MainLayout title="Bill Processing WebSocket">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please log in to access bill processing.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Login
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={workflowName}>
      {/* Header with WebSocket status */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {workflowName}
              </h1>
              <div className="flex items-center gap-2 text-sm">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  executionStatus === 'running' ? 'bg-blue-100 text-blue-700' :
                  executionStatus === 'completed' ? 'bg-green-100 text-green-700' :
                  executionStatus === 'failed' ? 'bg-red-100 text-red-700' :
                  executionStatus === 'awaiting_user' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {executionStatus}
                </span>
                {currentStep && (
                  <span className="text-gray-500 dark:text-gray-400">
                    Step: {currentStep}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* WebSocket Status */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
              wsConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {wsConnected ? 'Connected' : 'Disconnected'}
            </div>

            {runId && (
              <span className="text-xs text-gray-500 font-mono">
                {runId.substring(0, 8)}...
              </span>
            )}

            {isAdmin && runId && (
              <button
                onClick={handleDebug}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
              >
                Debug
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden h-[calc(100vh-12rem)]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <BillWSChatMessages
            key={runId || 'initial'}
            messages={messages}
            isLoading={isLoading}
            workflowName={workflowName}
            error={error}
            runId={runId}
            workflowId={workflowId || '22'}
            token={token}
            sendMessage={sendMessage}
            executionStatus={executionStatus}
            currentStep={currentStep}
            isReopenedSession={!!initialRunId}
            agentId={agentId}
          />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <BillWSChatInput
            onSendMessage={sendMessage}
            isLoading={isLoading}
            placeholder="Send a message to start bill processing..."
            disabled={!token}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default BillWSChatPage;
